import { useRef, useCallback } from 'react';
import { useInfiniteQuery, useQueryClient } from 'react-query';
import { fetcher, performanceReportingApiUrl } from 'data_sources/constants';
import type { ReportDataRequest, ReportRunResponse } from '../_types';

export const getRunReportData = async (
  orgId: string,
  params: ReportDataRequest,
  nextToken?: string,
): Promise<ReportRunResponse> => {
  const searchParams = new URLSearchParams();
  searchParams.set('fieldIds', params.fieldIds.join(','));
  searchParams.set('startDate', params.startDate);
  searchParams.set('endDate', params.endDate);

  if (params.templateIds && params.templateIds.length > 0) {
    searchParams.set('templateIds', params.templateIds.join(','));
  }

  if (nextToken) {
    searchParams.set('nextToken', nextToken);
  }

  const url = `${performanceReportingApiUrl()}/performance/reporting/v1/agency/${orgId}/report-data?${searchParams.toString()}`;

  return fetcher({
    url,
    method: 'GET',
  }) as Promise<ReportRunResponse>;
};

const QUERY_KEY = 'reportDataRun';

export const useRunReport = (orgId: string) => {
  const paramsRef = useRef<ReportDataRequest | null>(null);
  const queryClient = useQueryClient();

  const query = useInfiniteQuery<ReportRunResponse, Error>(
    [QUERY_KEY, orgId],
    async ({ pageParam }) => {
      const params = paramsRef.current;
      if (!params) {
        return { templates: [], content: [], count: 0, offset: 0 };
      }
      return getRunReportData(orgId, params, pageParam as string | undefined);
    },
    {
      enabled: false,
      cacheTime: 0,
      getNextPageParam: (lastPage) => lastPage?.nextToken ?? undefined,
    },
  );

  const runReport = useCallback(
    (params: ReportDataRequest) => {
      paramsRef.current = params;
      queryClient.removeQueries([QUERY_KEY, orgId]);
      query.refetch();
    },
    [orgId, query, queryClient],
  );

  return { ...query, runReport };
};
